import type { TranslationParams } from '../i18n'

const pad = (n: number) => String(n).padStart(2, '0')

/** ISO instant -> `dd/MM/yyyy HH:mm` (24h) using the browser's LOCAL wall clock.
 *  Deterministic format regardless of locale. Falls back to the raw input on an
 *  unparseable string so we never render "NaN/NaN/NaN". */
export function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Whole minutes -> compact i18n duration: "30 min", "1 h", "1 h 30 min". */
export function formatDuration(
  minutes: number,
  t: (key: string, params?: TranslationParams) => string,
): string {
  const total = Math.max(0, Math.round(minutes))
  if (total < 60) return t('common.durationMin', { m: total })
  const h = Math.floor(total / 60)
  const m = total % 60
  return m === 0 ? t('common.durationHour', { h }) : t('common.durationHourMin', { h, m })
}

/** `YYYY-MM-DD` calendar date -> short weekday + day + month in the UI locale:
 *  "Thu, 29 Oct" (en) / "qui., 29 de out." (pt-BR). The date is a wall-clock
 *  day (already in the building timezone), so it is formatted at noon UTC with
 *  `timeZone: 'UTC'` and never shifts by the viewer's offset. English uses the
 *  en-GB day-before-month order to match the app's dd/MM convention. Falls
 *  back to the raw input on a malformed string. */
/** "Mon 2" / "seg. 2": short enough for a day tab at phone width. */
export function formatWeekdayDay(ymd: string, locale: string): string {
  const parts = ymd.split('-').map(Number)
  if (parts.length !== 3 || parts.some(Number.isNaN)) return ymd
  const [y, m, d] = parts as [number, number, number]
  const date = new Date(Date.UTC(y, m - 1, d, 12))
  if (Number.isNaN(date.getTime())) return ymd
  // Built from parts: pt-BR's own pattern is "seg., 2".
  const weekday = new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : locale, {
    timeZone: 'UTC',
    weekday: 'short',
  }).format(date)
  return `${weekday} ${d}`
}

export function formatWeekdayDate(ymd: string, locale: string): string {
  const parts = ymd.split('-').map(Number)
  if (parts.length !== 3 || parts.some(Number.isNaN)) return ymd
  const [y, m, d] = parts as [number, number, number]
  const date = new Date(Date.UTC(y, m - 1, d, 12))
  if (Number.isNaN(date.getTime())) return ymd
  return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : locale, {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date)
}
